/**
 * One prospect's panel: every reason with its citation, what is missing to score them better, and the
 * advisor's latest feedback. Loaded only while the panel is open.
 *
 * A prospect can be gone by the time its panel is opened — an old link, or a contact who dropped off
 * the list at the last scoring pass. That is `notFound`; any other failure is `failed`, which can be
 * tried again.
 */
import { useQuery } from '@tanstack/react-query';
import { ApiClientError } from '@client/api/apiClient';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import type { Prospecting } from '@client/api/types';

export function useProspectDetail(provider: Prospecting.Provider | null, prospectId: string | null) {
  const result = useQuery({
    queryKey: queryKeys.prospecting.prospect(provider ?? '', prospectId ?? ''),
    queryFn: () => prospectingApi.prospects.detail(provider!, prospectId!),
    enabled: !!provider && !!prospectId,
  });

  const notFound = result.error instanceof ApiClientError && result.error.status === 404;

  return {
    detail: result.data,
    isLoading: result.isLoading,
    notFound,
    failed: !!result.error && !notFound,
    retry: () => result.refetch(),
  };
}
